import { PageHero } from "@/components/PageHero";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, api, getSessionId, getToken } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Share2, Download, RefreshCw, Loader2, ArrowLeft, ArrowRight, Lock, Stethoscope, Plus, X, Wand2, Sparkles, Save } from "lucide-react";
import DripWelcomeBanner from "@/components/DripWelcomeBanner";
import useDripReferral from "@/hooks/useDripReferral";
import { track, trackOnce, EVENTS } from "@/lib/analytics";

/* Care Team Summary — auto-generated from My Story, recent chats, behavior log,
   wellness, and education. Caregivers download as PDF; sharing happens through
   the unified Care Summary builder (/care-summary) — the platform's ONE share
   surface (legacy per-page share links retired, Jun 2026).
*/

const SECTION_TITLES = {
    my_story: "Who they are",
    intake: "Clinical snapshot",
    wellness: "Caregiver wellness (last 14 days)",
    behaviors: "Behavior log (last 14 days)",
    themes: "What they've been asking about",
    chats: "In the caregiver's own words",
};

export default function CareTeamSummary() {
    const sid = getSessionId();
    // Day 21 (and any future) drip email that lands here — the email
    // subject is "Got a doctor's visit coming up? Lane can help." so this
    // page is intentionally the appointment-prep destination.
    const drip = useDripReferral();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        try {
            const r = await api.get(`/care-team/summary/${sid}`);
            setSummary(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [sid]);

    useEffect(() => {
        trackOnce(`appt_prep_${sid}`, EVENTS.APPOINTMENT_PREP_OPENED, { session_id: sid, source: "journey_appointments" });
        trackOnce(`appt_prep_started_${sid}`, EVENTS.APPOINTMENT_PREP_STARTED, { session_id: sid, source: "journey_appointments" });
    }, [sid]);

    const refresh = async () => {
        setRefreshing(true);
        try {
            const r = await api.post(`/care-team/summary/${sid}/generate`);
            setSummary(r.data);
            track(EVENTS.APPOINTMENT_PREP_COMPLETED, { session_id: sid, source: "journey_appointments" });
            toast.success("Summary refreshed with the latest from your week.");
        } catch (e) {
            toast.error("Couldn't refresh just now. Try again in a moment.");
        } finally {
            setRefreshing(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const token = getToken();
            const res = await fetch(`${API}/care-team/summary/${sid}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `care-team-summary-${sid.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Couldn't download the PDF. Try again.");
        }
    };


    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[#6E7A74] py-12">
                <Loader2 className="animate-spin" size={18} /> Putting your summary together…
            </div>
        );
    }

    return (
        <div data-clarity-mask="true" className="max-w-3xl space-y-8 animate-fade-in-up">
            {/* Day 21 drip landing banner — appears when the caregiver
                arrives via the "doctor's visit coming up?" email. Doesn't
                clash with the always-visible header because the banner
                lives ABOVE the "Care Team Summary" eyebrow. */}
            {drip.kind && !drip.dismissed && (
                <DripWelcomeBanner
                    kind={drip.kind}
                    testid="appointment-drip-banner"
                    message={
                        drip.kind === "day21"
                            ? `Got a doctor's visit coming up for ${drip.lovedOneName || "your loved one"}? This page has your prep — questions to ask, symptoms to mention, and the summary to hand over.`
                            : `Lane's weekly check-in. Here's your care-team-ready note for ${drip.lovedOneName || "your loved one"}.`
                    }
                    onDismiss={drip.dismiss}
                />
            )}
            <PageHero
                kicker="Care Team Summary"
                kickerIcon={FileText}
                title="Note for the care team."
                subtitle={<>Memory Lane has been quietly listening — to what you tell Lane, to the behavior log, to your wellness check-ins. Here&apos;s a single, care-team-ready note you can hand over, with room for the specific questions you want to ask.</>}
            />

            {/* Book a free Nurse Practitioner 15-min — gentle CTA right above
                the questions section so the caregiver sees it before they
                start drafting their own list. */}
            <Link
                to="/book"
                data-testid="care-team-book-np"
                className="ml-card ml-card-hover p-5 md:p-6 bg-coral-light/40 !border-coral-light/60 flex items-start gap-4 group no-underline"
            >
                <div className="shrink-0 w-11 h-11 rounded-2xl bg-white flex items-center justify-center">
                    <Stethoscope strokeWidth={1.6} size={20} className="text-coral-dark" />
                </div>
                <div className="flex-1">
                    <p className="text-sm uppercase tracking-wider font-semibold text-[#6E7A74]">
                        Want to talk it through first?
                    </p>
                    <p className="mt-1 text-lg font-heading font-medium text-[#1F2624]">
                        Book 15 free minutes with a Nurse Practitioner.
                    </p>
                    <p className="mt-1 text-sm text-[#4C5551]">
                        Free, no commitment. We&apos;ll help you decide what to bring to your care team.
                    </p>
                </div>
                <ArrowRight strokeWidth={1.8} size={20} className="text-coral-dark group-hover:translate-x-1 transition-transform mt-1.5 shrink-0" />
            </Link>

            {/* Questions for the care team — merged from the old Appointment Prep page. */}
            <CareTeamQuestions sid={sid} />

            {/* Action bar */}
            <div className="flex flex-wrap gap-3" data-testid="care-team-actions">
                <button
                    type="button"
                    onClick={refresh}
                    disabled={refreshing}
                    data-testid="care-team-refresh"
                    className="px-5 py-2.5 rounded-2xl text-sm font-medium bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30 disabled:opacity-60 inline-flex items-center gap-2"
                >
                    {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Refresh from this week
                </button>
                <button
                    type="button"
                    onClick={downloadPdf}
                    data-testid="care-team-download"
                    className="px-5 py-2.5 rounded-2xl text-sm font-medium bg-teal-dark text-white hover:bg-teal inline-flex items-center gap-2"
                >
                    <Download size={14} /> Download PDF
                </button>
                <Link
                    to="/care-summary"
                    data-testid="care-team-share"
                    onClick={() => track(EVENTS.CARE_SUMMARY_SHARE_STARTED, { source: "journey_appointments" })}
                    className="px-5 py-2.5 rounded-2xl text-sm font-medium bg-white border border-cream-dark text-[#1F2624] hover:bg-cream-dark/30 inline-flex items-center gap-2 no-underline"
                >
                    <Share2 size={14} /> Share with care team
                </Link>
            </div>

            <SummaryPreview summary={summary} forShare={false} />

            <div className="pt-2">
                <Link to="/dashboard" className="text-sm text-[#6E7A74] inline-flex items-center gap-1.5 hover:text-[#4C5551]">
                    <ArrowLeft size={14} /> Back to your dashboard
                </Link>
            </div>
        </div>
    );
}

export function SummaryPreview({ summary, forShare = true }) {
    if (!summary) return null;
    // Clarity: this preview renders the loved one's full clinical story —
    // masked in session recordings regardless of the project-level setting.
    // "with_me" → "with me" — stored enum values read naturally (matches
    // the PDF renderer's _human()).
    const human = (v) => String(v ?? "").replace(/_/g, " ");
    const lo = summary.my_story?.loved_one || {};
    const cg = summary.my_story?.caregiver || {};
    const ib = summary.intake_brief || {};
    const medChanges = summary.medication_changes || { medications: [], recent_changes: "", allergies: "", source: "none" };
    const behaviorChanges = summary.behavior_changes_14d || { total_14d: 0, by_type: {}, new_what_helped: [] };
    const wellness = summary.wellness || {};
    const averages = wellness.averages_14d || {};
    const behaviors = summary.behaviors || [];
    const themes = summary.behavior_themes || {};
    const chatThemes = summary.chat_themes || {};
    const chats = (summary.chats || []).filter(c => c.role === "user").slice(-6);
    const appt = summary.appointment || {};
    const apptQuestions = (appt.questions || []).filter(q => q);
    const hasApptNotes = appt.medication_concerns || appt.recent_changes || appt.safety_concerns || appt.caregiver_concerns;

    return (
        <article className="ml-card p-6 md:p-8 bg-white space-y-7" data-testid="care-team-preview" data-clarity-mask="true">
            <div>
                <p className="text-sm text-[#6E7A74]">
                    Prepared by {summary.caregiver_name || "the caregiver"} for the care team of {summary.loved_one_name || "their loved one"}.
                </p>
                <p className="text-xs text-[#6E7A74] mt-1">Window: last {summary.window_days || 14} days · Generated {String(summary.generated_at || "").slice(0, 10)}</p>
                {!forShare && (
                    <p className="mt-3 text-xs text-[#6E7A74] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-2xl bg-cream-dark/40 border border-cream-dark" data-testid="care-team-preview-mode-banner">
                        You&apos;re seeing the caregiver view. The full <strong className="text-[#1F2624]">clinical snapshot</strong> — meds, recent medication changes, and behavior deltas — is built automatically and included when you share with the care team.
                    </p>
                )}
            </div>

            {(Object.values(lo).some(Boolean) || Object.values(cg).some(Boolean)) && (
                <Section title={SECTION_TITLES.my_story}>
                    <Dl rows={[
                        ["Who they were", lo.who_they_were],
                        ["Profession", lo.profession],
                        ["What they loved", lo.what_they_loved],
                        ["What matters now", lo.what_matters_now],
                        ["Hobbies", lo.hobbies],
                        ["Caregiver's biggest fear", cg.biggest_fear],
                        ["What would help most", cg.what_would_help],
                    ]} />
                </Section>
            )}

            {/* Clinical snapshot — outbound only. The caregiver intentionally
                doesn't see this on their own preview page because it just
                mirrors data they already maintain elsewhere (Patient profile,
                Behavior log, GUIDE). Showing it back to them would be noisy
                duplication. The receiving care team needs the full picture. */}
            {forShare && (
                (Object.values(ib).some(v => v && (Array.isArray(v) ? v.length : true))
                    || medChanges.medications?.length > 0 || medChanges.recent_changes || medChanges.medications_text
                    || behaviorChanges.total_14d > 0) && (
                    <Section title={SECTION_TITLES.intake}>
                        <Dl rows={[
                            ["Estimated stage", ib.stage],
                            ["Living situation", human(ib.living_situation)],
                            ["Caregiver relationship", human(ib.relationship)],
                            ["Hours/week of care", ib.hours_per_week],
                            ["Main concerns", (ib.main_concerns || []).map(human).join(", ")],
                            ["Safety concerns", (ib.safety_concerns || []).filter(s => s !== "none").map(human).join(", ")],
                        ]} />

                        {/* Current medications + recent med changes — the
                            piece a primary-care team most asks for. */}
                        {(medChanges.medications?.length > 0 || medChanges.medications_text || medChanges.recent_changes || medChanges.allergies) && (
                            <div className="mt-4 rounded-2xl bg-cream-light border border-cream-dark p-4" data-testid="care-team-meds-block">
                                <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">Current medications</p>
                                {medChanges.medications?.length > 0 && (
                                    <ul className="text-sm text-[#1F2624] space-y-1">
                                        {medChanges.medications.map((m, i) => (
                                            <li key={i}>
                                                <strong>{m.name}</strong>
                                                {[m.dose, m.schedule, m.purpose].filter(Boolean).length > 0 &&
                                                    ` — ${[m.dose, m.schedule, m.purpose].filter(Boolean).join(" · ")}`}
                                                {m.status === "discontinued"
                                                    ? <em className="text-coral-dark font-semibold"> (discontinued{m.effective_date ? ` as of ${m.effective_date}` : ""})</em>
                                                    : m.change && !["unchanged", "current"].includes(m.change) &&
                                                        <em className="text-coral-dark font-semibold"> ({m.change}{m.effective_date ? ` as of ${m.effective_date}` : ""})</em>}
                                                {m.conflict && <span className="text-[10px] text-[#8A6D1A] font-semibold"> · conflicting records — confirm with the care team</span>}
                                                {m.source === "journey" &&
                                                    <span className="text-[10px] text-teal-dark font-semibold"> · document-verified</span>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {medChanges.medications_text && (
                                    <p className="text-sm text-[#1F2624] whitespace-pre-wrap leading-relaxed">{medChanges.medications_text}</p>
                                )}
                                {medChanges.allergies && (
                                    <p className="text-sm text-[#1F2624] mt-2"><strong>Allergies:</strong> {medChanges.allergies}</p>
                                )}
                                {medChanges.recent_changes && (
                                    <p className="text-sm text-[#1F2624] mt-2"><strong>Recent medication changes (caregiver-reported):</strong> {medChanges.recent_changes}</p>
                                )}
                            </div>
                        )}

                        {/* Behavior deltas over the 14-day window */}
                        {behaviorChanges.total_14d > 0 && (
                            <div className="mt-3 rounded-2xl bg-cream-light border border-cream-dark p-4" data-testid="care-team-behavior-block">
                                <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">Behavior changes — last 14 days</p>
                                <p className="text-sm text-[#1F2624]">
                                    <strong>{behaviorChanges.total_14d} episode{behaviorChanges.total_14d === 1 ? "" : "s"}</strong> logged
                                    {Object.keys(behaviorChanges.by_type || {}).length > 0 && (
                                        <> · {Object.entries(behaviorChanges.by_type)
                                            .sort((a, b) => b[1] - a[1])
                                            .map(([k, v]) => `${k} (${v})`)
                                            .join(", ")}</>
                                    )}
                                    {behaviorChanges.highest_severity != null && (
                                        <> · highest severity reported: <strong>{behaviorChanges.highest_severity}/5</strong></>
                                    )}
                                </p>
                                {(behaviorChanges.new_what_helped || []).length > 0 && (
                                    <p className="text-sm text-[#1F2624] mt-2">
                                        <strong>What helped at home:</strong> {behaviorChanges.new_what_helped.join("; ")}
                                    </p>
                                )}
                            </div>
                        )}
                    </Section>
                )
            )}

            {forShare && summary.journey && (
                <Section title={`From ${summary.loved_one_name ? `${summary.loved_one_name.split(" ")[0]}'s` : "the"} Journey record (document-verified)`}>
                    <div data-testid="care-team-journey-block">
                        {(summary.journey.diagnoses || []).length > 0 && (
                            <p className="text-sm text-[#1F2624]"><strong>Documented diagnoses:</strong> {summary.journey.diagnoses.join(", ")}</p>
                        )}
                        {summary.journey.stage?.label && (
                            <p className="text-sm text-[#1F2624] mt-1"><strong>Documented stage:</strong> {summary.journey.stage.label}{summary.journey.stage.evidence ? ` — ${summary.journey.stage.evidence}` : ""}</p>
                        )}
                        {(summary.journey.documents || []).slice(0, 5).map(x => (
                            <p key={x.id} className="text-sm text-[#2A3330] mt-2">• <strong>{x.date}</strong> — {x.filename} <span className="text-xs text-[#9CA6A1]">({x.category})</span></p>
                        ))}
                        {(summary.journey.events || []).filter(e => e.label).slice(0, 8).map(e => (
                            <p key={e.id} className="text-sm text-[#2A3330] mt-1">{e.date}: {e.label}</p>
                        ))}
                        <p className="text-xs text-[#6E7A74] mt-3">Extracted from documents the caregiver uploaded and explicitly approved. Originals are preserved in Memory Lane.</p>
                    </div>
                </Section>
            )}

            {(apptQuestions.length > 0 || hasApptNotes) && (
                <Section title="Questions and notes for the care team">
                    {(appt.provider_name || appt.appointment_date) && (
                        <p className="text-sm text-[#4C5551] mb-2">
                            {appt.provider_name && <><span className="font-medium">Provider:</span> {appt.provider_name}</>}
                            {appt.provider_name && appt.appointment_date && " · "}
                            {appt.appointment_date && <><span className="font-medium">Date:</span> {appt.appointment_date}</>}
                        </p>
                    )}
                    {apptQuestions.length > 0 && (
                        <ol className="space-y-1.5 text-sm text-[#1F2624] mb-3 list-decimal pl-5">
                            {apptQuestions.map((q, i) => <li key={i}>{q}</li>)}
                        </ol>
                    )}
                    <Dl rows={[
                        ["Recent changes", appt.recent_changes],
                        ["Medication concerns", appt.medication_concerns],
                        ["Safety concerns", appt.safety_concerns],
                        ["How I'm holding up", appt.caregiver_concerns],
                    ]} />
                </Section>
            )}

            {Object.values(averages).some(v => v !== null && v !== undefined) && (
                <Section title={SECTION_TITLES.wellness}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(averages).map(([k, v]) => (
                            <div key={k} className="px-3 py-2 rounded-2xl bg-cream-dark/30 border border-cream-dark">
                                <p className="text-xs uppercase tracking-wider text-[#6E7A74]">{k.replace("_", " ")}</p>
                                <p className="text-lg font-heading text-[#1F2624]">{v === null || v === undefined ? "—" : v}<span className="text-xs text-[#6E7A74]">/10</span></p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {behaviors.length > 0 && (
                <Section title={SECTION_TITLES.behaviors}>
                    {Object.keys(themes).length > 0 && (
                        <p className="text-sm text-[#4C5551] mb-2">
                            <span className="font-medium">Themes:</span>{" "}
                            {Object.entries(themes).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                        </p>
                    )}
                    <ul className="space-y-1.5 text-sm text-[#1F2624]">
                        {behaviors.slice(0, 8).map(b => (
                            <li key={b.id || `${b.behavior_type}-${b.created_at}`}>
                                <span className="text-[#6E7A74]">{String(b.created_at || "").slice(0, 10)}</span> · <strong>{b.behavior_type || "episode"}</strong>
                                {b.trigger ? ` — trigger: ${b.trigger}` : ""}{b.what_helped ? `. What helped: ${b.what_helped}.` : ""}
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {Object.keys(chatThemes).length > 0 && (
                <Section title={SECTION_TITLES.themes}>
                    <p className="text-sm text-[#4C5551]">
                        {Object.entries(chatThemes).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} (${v})`).join(" · ")}
                    </p>
                </Section>
            )}

            {chats.length > 0 && (
                <Section title={SECTION_TITLES.chats}>
                    <div className="space-y-3">
                        {chats.map((m, i) => (
                            <div key={i}>
                                <p className="italic text-sm text-[#4C5551] leading-relaxed">
                                    “{(m.content || "").slice(0, 220)}{(m.content || "").length > 220 ? "…" : ""}”
                                </p>
                                <p className="text-xs text-[#6E7A74] mt-0.5">— {String(m.created_at || "").slice(0, 10)}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            <p className="text-xs text-[#6E7A74] pt-3 border-t border-cream-dark">
                This summary is generated by Memory Lane Care Navigator. It is not a medical record.
            </p>
        </article>
    );
}

function CareTeamQuestions({ sid }) {
    const [form, setForm] = useState({
        appointment_date: "",
        provider_name: "",
        questions: [],
        medication_concerns: "",
        recent_changes: "",
        safety_concerns: "",
        caregiver_concerns: "",
    });
    const [newQ, setNewQ] = useState("");
    const [aiQuestions, setAiQuestions] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load most recent saved appointment prep so the caregiver returns to
    // a populated form, not a blank one.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get(`/appointment/${sid}`);
                if (!active) return;
                const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                if (list.length > 0) {
                    const last = list[0];
                    setForm({
                        appointment_date: last.appointment_date || "",
                        provider_name: last.provider_name || "",
                        questions: last.questions || [],
                        medication_concerns: last.medication_concerns || "",
                        recent_changes: last.recent_changes || "",
                        safety_concerns: last.safety_concerns || "",
                        caregiver_concerns: last.caregiver_concerns || "",
                    });
                }
            } catch { /* ignore — fresh start is fine */ }
        })();
        return () => { active = false; };
    }, [sid]);

    const addQuestion = (incoming) => {
        const source = typeof incoming === "string" ? incoming : newQ;
        const t = source.trim();
        if (!t) return;
        if (form.questions.includes(t)) {
            toast("Already on your list.");
            return;
        }
        setForm(f => ({ ...f, questions: [...f.questions, t] }));
        setNewQ("");
    };
    const removeQuestion = (i) => {
        setForm(f => ({ ...f, questions: f.questions.filter((_, idx) => idx !== i) }));
    };

    const generateAiQuestions = async () => {
        setAiLoading(true);
        try {
            const r = await api.post(`/appointment/suggest-questions/${sid}`);
            setAiQuestions(r.data.questions || []);
            if (!(r.data.questions || []).length) {
                toast("AI didn't suggest anything new — your list is solid.");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't generate questions.");
        } finally {
            setAiLoading(false);
        }
    };
    const addAiQuestion = (q) => {
        if (form.questions.includes(q)) { toast("Already on your list."); return; }
        setForm(f => ({ ...f, questions: [...f.questions, q] }));
        setAiQuestions(prev => prev.filter(x => x !== q));
    };
    const addAllAi = () => {
        setForm(f => {
            const existing = new Set(f.questions);
            return { ...f, questions: [...f.questions, ...aiQuestions.filter(q => !existing.has(q))] };
        });
        setAiQuestions([]);
        toast.success("All AI questions added.");
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/appointment", { session_id: sid, ...form });
            // Also refresh the Care Team Summary so the PDF + share view pick
            // up the new questions immediately.
            try { await api.post(`/care-team/summary/${sid}/generate`); } catch { /* best-effort */ }
            toast.success("Saved to your care-team note.");
        } catch {
            toast.error("Couldn't save just now.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="ml-card p-5 md:p-7 bg-white" data-testid="care-team-questions">
            <h2 className="text-2xl font-heading font-medium text-[#12776F]">Questions and notes for the care team</h2>
            <p className="text-sm text-[#6E7A74] mt-1">
                Anything you add here lands in your downloadable note and the link you share.
            </p>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-[#4C5551] mb-1.5">Next appointment</label>
                    <input
                        type="date"
                        data-testid="ctq-date"
                        className="w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-soft"
                        value={form.appointment_date}
                        onChange={e => setForm({ ...form, appointment_date: e.target.value })}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-[#4C5551] mb-1.5">Provider</label>
                    <input
                        data-testid="ctq-provider"
                        placeholder="e.g. Dr. Patel, Neurology"
                        className="w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-soft"
                        value={form.provider_name}
                        onChange={e => setForm({ ...form, provider_name: e.target.value })}
                    />
                </div>
            </div>

            <div className="mt-5">
                <label className="block text-sm font-medium text-[#4C5551] mb-1.5">Questions you want to ask</label>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <input
                        data-testid="ctq-new-question"
                        value={newQ}
                        onChange={e => setNewQ(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addQuestion(); } }}
                        placeholder="Type a question and press Add"
                        className="flex-1 min-w-[200px] bg-white border border-cream-dark rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-soft"
                    />
                    <button
                        type="button"
                        onClick={() => addQuestion()}
                        data-testid="ctq-add-question"
                        className="px-4 py-3 rounded-2xl bg-teal-dark text-white text-sm font-medium inline-flex items-center gap-1.5"
                    >
                        <Plus size={14} strokeWidth={1.8} /> Add
                    </button>
                </div>
                {form.questions.length > 0 && (
                    <ul className="space-y-2">
                        {form.questions.map((q, i) => (
                            <li key={i} data-testid={`ctq-q-${i}`} className="flex items-center justify-between gap-3 bg-cream-dark/40 rounded-2xl px-4 py-2.5">
                                <span className="text-[#1F2624] text-sm">{i + 1}. {q}</span>
                                <button type="button" onClick={() => removeQuestion(i)} data-testid={`ctq-q-remove-${i}`} className="text-coral-dark">
                                    <X size={16} strokeWidth={1.6} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {/* AI suggest */}
                <div className="mt-4 rounded-2xl bg-teal-soft/40 border border-teal-soft p-4" data-testid="ctq-ai-section">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <Wand2 size={16} className="text-teal-dark" />
                            <p className="font-medium text-teal-dark">Going blank? Let Lane help.</p>
                        </div>
                        {aiQuestions.length === 0 && !aiLoading && (
                            <button
                                type="button"
                                onClick={generateAiQuestions}
                                data-testid="ctq-ai-generate"
                                className="px-3 py-1.5 rounded-2xl bg-teal-dark text-white text-xs font-medium inline-flex items-center gap-1.5"
                            >
                                <Sparkles size={12} strokeWidth={1.8} /> Generate
                            </button>
                        )}
                    </div>
                    {aiLoading && <p className="mt-2 text-sm text-[#4C5551]" data-testid="ctq-ai-loading">Thinking through your situation…</p>}
                    {aiQuestions.length > 0 && (
                        <div className="mt-3 space-y-1.5" data-testid="ctq-ai-results">
                            {aiQuestions.map((q, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => addAiQuestion(q)}
                                    data-testid={`ctq-ai-q-${i}`}
                                    className="w-full text-left flex items-start gap-2 px-3 py-2 rounded-xl bg-white border border-teal-soft hover:bg-teal-soft text-sm text-[#1F2624]"
                                >
                                    <Plus size={12} strokeWidth={2} className="mt-1 text-teal-dark shrink-0" />
                                    <span>{q}</span>
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={addAllAi}
                                data-testid="ctq-ai-add-all"
                                className="mt-1 px-3 py-1 text-xs font-medium text-teal-dark hover:bg-teal-soft rounded-2xl"
                            >
                                Add all to my list
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    { key: "recent_changes", label: "Recent changes I've noticed", placeholder: "What's different in the last few weeks." },
                    { key: "medication_concerns", label: "Medication concerns", placeholder: "Anything that started or stopped, side effects." },
                    { key: "safety_concerns", label: "Safety concerns", placeholder: "Falls, driving, wandering, stove…" },
                    { key: "caregiver_concerns", label: "How I'm holding up", placeholder: "Burnout, sleep, what I need." },
                ].map(f => (
                    <label key={f.key} className="block">
                        <span className="block text-sm font-medium text-[#4C5551] mb-1.5">{f.label}</span>
                        <textarea
                            data-testid={`ctq-${f.key}`}
                            value={form[f.key]}
                            onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                            placeholder={f.placeholder}
                            className="w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-teal-soft text-sm"
                        />
                    </label>
                ))}
            </div>

            <div className="mt-5 flex">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="ctq-save"
                    className="px-5 py-2.5 rounded-2xl bg-teal-dark text-white font-medium hover:bg-teal disabled:opacity-60 inline-flex items-center gap-2"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save to my note
                </button>
            </div>
        </section>
    );
}

function Section({ title, children }) {    return (
        <section>
            <h3 className="text-lg font-heading font-medium text-teal-dark mb-2">{title}</h3>
            {children}
        </section>
    );
}

function Dl({ rows }) {
    const cleaned = rows.filter(([_, v]) => v !== undefined && v !== null && v !== "");
    if (!cleaned.length) return <p className="text-sm text-[#6E7A74]">Nothing recorded yet.</p>;
    return (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
            {cleaned.map(([k, v]) => (
                <React.Fragment key={k}>
                    <dt className="text-[#6E7A74]">{k}</dt>
                    <dd className="text-[#1F2624]">{String(v)}</dd>
                </React.Fragment>
            ))}
        </dl>
    );
}
